<script lang="ts">
export interface  Emits {
  complete: [value: string]
  change: [value: string]
  'update:modelValue': [value: string]
};

export interface Props {
  modelValue?: string,
  fields?: number,
  inputMode?: HTMLAttributes['inputmode'],
  inputType?: 'text' | 'number',
  fieldWidth?: number,
  fieldHeight?: number,
  autoFocus?: boolean,
  disabled?: boolean,
};
</script>

<script setup lang="ts">
import { computed, nextTick, onBeforeUpdate, onMounted, ref } from 'vue';
import type { HTMLAttributes } from 'vue';

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  fields: 6,
  inputMode: 'numeric',
  inputType: 'number',
  fieldWidth: 35,
  fieldHeight: 35,
  autoFocus: false,
  disabled: false,
});

const emit = defineEmits<Emits>();

const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;

const otp = ref<(string | null)[]>([]);
const activeInput = ref(0);
let itemRefs: HTMLInputElement[] = [];

const inputTypeValue = computed(() =>
  ['text', 'number'].includes(props.inputType)
    ? 'text'
    : props.inputType,
);

const setItemRef = (el: HTMLInputElement) => {
  if (el) {
    itemRefs.push(el);
  }
};

const handleFocus = (index: number) => {
  activeInput.value = index;
};

const focusInput = (input: number) => {
  activeInput.value = Math.max(Math.min(props.fields - 1, input), 0);
  const itemInput = itemRefs[activeInput.value];
  if(itemInput) {
      itemInput.focus();
      itemInput.select();
  }
};

const focusNextInput = () => {
  if (activeInput.value + 1 <= props.fields - 1) {
    focusInput(activeInput.value + 1);
  }
};

const focusPrevInput = () => {
  focusInput(activeInput.value - 1);
};

const handlerChange = (value: InputEvent) => {
  const target = value.target as HTMLInputElement;
  const val = target?.value || '';

  if (props.inputType === 'number' && val.replace(/[^\d]/gi, '') === '') {
    otp.value[activeInput.value] = null;
    nextTick(() => {
      otp.value[activeInput.value] = '';
    });
  } else {
    handleChange(val);
  }
};

const handleChange = (value: string) => {
  changeCodeAtFocus(value);
  focusNextInput();
};

const changeCodeAtFocus = (value: string) => {
  otp.value[activeInput.value] = value;
  emit('update:modelValue', otp.value.join(''));
  emit('change', otp.value.join(''));
  checkFilledAllInputs();
};

const checkFilledAllInputs = () => {
  if (otp.value.join('').length === props.fields) {
    emit('update:modelValue', otp.value.join(''));
    emit('complete', otp.value.join(''));
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  switch (event.keyCode) {
    case BACKSPACE:
      event.preventDefault();
      changeCodeAtFocus('');
      focusPrevInput();
      break;
    case DELETE:
      event.preventDefault();
      changeCodeAtFocus('');
      break;
    case LEFT_ARROW:
      event.preventDefault();
      focusPrevInput();
      break;
    case RIGHT_ARROW:
      event.preventDefault();
      focusNextInput();
      break;
    default:
      break;
  }
};
const handleOnPaste = (event: ClipboardEvent) => {
  event.preventDefault();
  if (!event.clipboardData) {
    return;
  }

  const pastedData = event.clipboardData
    .getData('text/plain')
    .slice(0, props.fields - activeInput.value)
    .split('');

  if ((props.inputType === 'number' ? !!pastedData.join('').match(/^\d+$/) : true)) {
    const currentCharsInOtp = otp.value.slice(0, activeInput.value);
    const combinedWithPastedData = currentCharsInOtp.concat(pastedData);

    combinedWithPastedData.slice(0, props.fields).forEach(function(value, i) {
      otp.value[i] = value;
    });

    focusInput(combinedWithPastedData.slice(0, props.fields).length);
    return checkFilledAllInputs();
  }
};

onBeforeUpdate(() => {
  itemRefs = [];
});

onMounted(() => {
  if (props.autoFocus) {
    const itemInput = itemRefs[activeInput.value];
    if (itemInput) {
      itemInput.focus();
      itemInput.select();
    }
  }
});
</script>

<template>
  <div class="tfa-code-input">
    <template
      v-for="(item, index) in fields"
      :key="index"
    >
      <input
        :ref="(el) => setItemRef(el as HTMLInputElement)"
        :disabled="disabled"
        :inputmode="inputMode"
        :style="{width: `${fieldWidth}px`, height: `${fieldHeight}px`}"
        :type="inputTypeValue"
        :value="otp[index]"
        class="tfa-code-input__element"
        max="9"
        maxlength="1"
        min="0"
        pattern="[0-9]"
        @focus="handleFocus(index)"
        @input="handlerChange"
        @keydown="handleKeyDown"
        @paste="handleOnPaste"
      >
    </template>
  </div>
</template>

<style lang="scss">
.tfa-code-input {
  font-family: Open Sans, sans-serif;
  white-space: nowrap;

  &__element {
    width: 35px;
    height: 35px;
    border: 1px solid #d9d9d9;
    caret-color: #000;
    padding: 0;
    text-align: center;
    outline: none;
    color: var(--text-body);
    border-right: none;
    font-size: 25px;
    font-weight: 500;
    font-family: var(--main-font);
    box-sizing: border-box;

    &:focus + input {
      border-left: 1px solid var(--text-primary);
    }

    &:focus {
      border: 1px solid var(--text-primary);
      border-right: none;
    }

    &:first-child {
      border-radius: 8px 0 0 8px;
    }

    &:last-child {
      border-radius: 0 8px 8px 0;
      border-right: 1px solid #d9d9d9;

      &:focus {
        border-right: 1px solid var(--text-primary);
      }
    }
  }
}
</style>
